import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import {
  kbProfileService,
  type KbProfile,
  type KbProfileSubmitRequest,
  type KbProfileUpdateRequest,
} from '@/services/kbProfileService';
import type { OAuthCallbackResponse } from '@/services/metaOauthService';
import {
  onboardingService,
  type OnboardingState,
  type MarkableStep,
} from '@/services/onboardingService';
import { tenantProfileService, type TenantProfile } from '@/services/tenantProfileService';

/** F027 — one of the OAuth section's terminal states.
 *
 * ``connected`` carries the callback payload from the
 * ``/onboarding/meta/callback`` POST so the timeline can render a success
 * card after redirecting back to ``/bot``. The ``error_*`` variants
 * map 1:1 to the stable error codes the backend returns.
 *
 * F116 — ``error_no_business`` and ``error_multiple`` removed; the
 * Instagram Login API no longer surfaces those conditions.
 */
export type MetaOauthOutcome =
  | { kind: 'idle' }
  | { kind: 'connected'; payload: OAuthCallbackResponse }
  | { kind: 'error_denied' }
  | { kind: 'error_state_expired' }
  | { kind: 'error_transient' }
  | { kind: 'error_unknown'; message: string };

/** F028 — Stripe Checkout return-path outcomes.
 *
 * The user leaves the SPA for Stripe Checkout, then returns via
 * ``/onboarding/stripe/success`` or ``/onboarding/stripe/cancel``. Those
 * pages set this outcome before navigating to ``/bot``; the
 * ``StripeSection`` inside the timeline reads it to render a transient
 * banner (cancelled / polling timeout) above the picker.
 *
 * On webhook success the onboarding state advances to ``completed`` and
 * the timeline collapses everything green — no outcome is needed for the
 * happy path (the success page just polls then navigates).
 */
export type StripeOutcome = { kind: 'idle' } | { kind: 'cancelled' } | { kind: 'timed_out' };

interface OnboardingContextValue {
  state: OnboardingState | null;
  profile: TenantProfile | null;
  /** F035: the structured KB profile (minimum-viable subset) backing the
   * reflective form. Used to rehydrate after the draft is committed. */
  kbProfile: KbProfile | null;
  loading: boolean;
  refresh: () => Promise<void>;
  markStepCompleted: (step: MarkableStep) => Promise<void>;
  /** F127 — dismiss the first-run welcome card. Optimistic: the local
   * ``welcome_seen_at`` flips immediately so the timeline reveals without
   * waiting on the PATCH; the server write fires in the background. */
  markWelcomeSeen: () => void;
  savePartialKb: (payload: Record<string, unknown> | null) => Promise<void>;
  /** F035: commit the reflective form's answers → advances timeline to
   * ``preview`` + clears the draft. Refreshes state + profile afterwards. */
  submitKbProfile: (payload: KbProfileSubmitRequest) => Promise<void>;
  /** F036: persist enrichment field changes to the KB profile and update the
   * local context state so all consumers see the latest profile immediately.
   */
  updateKbProfile: (changes: KbProfileUpdateRequest) => Promise<void>;
  /** F027: ephemeral handoff from ``MetaCallbackPage`` → ``MetaOauthSection``.
   * Cleared by the section once consumed.
   */
  metaOauthOutcome: MetaOauthOutcome;
  setMetaOauthOutcome: (outcome: MetaOauthOutcome) => void;
  /** F028: ephemeral handoff from ``Stripe{Success,Cancel}Page`` →
   * ``StripeSection``. Cleared by the section once the banner has been
   * shown / acknowledged.
   */
  stripeOutcome: StripeOutcome;
  setStripeOutcome: (outcome: StripeOutcome) => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [profile, setProfile] = useState<TenantProfile | null>(null);
  const [kbProfile, setKbProfile] = useState<KbProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [metaOauthOutcome, setMetaOauthOutcome] = useState<MetaOauthOutcome>({
    kind: 'idle',
  });
  const [stripeOutcome, setStripeOutcome] = useState<StripeOutcome>({
    kind: 'idle',
  });

  // F070 — `refresh()` is the explicit "re-read from the server" affordance
  // (post-OAuth auto-refresh, manual retries): it bypasses the shell cache.
  // The mount hydration below uses the cached path so provider remounts
  // (404 tree, OAuth/Stripe round trips) within the TTL skip the network.
  const fetchAll = useCallback(async (fresh: boolean) => {
    const [nextState, nextProfile, nextKbProfile] = await Promise.all([
      onboardingService.getState({ fresh }),
      tenantProfileService.get({ fresh }),
      kbProfileService.get({ fresh }),
    ]);
    setState(nextState);
    setProfile(nextProfile);
    setKbProfile(nextKbProfile);
  }, []);

  const refresh = useCallback(() => fetchAll(true), [fetchAll]);

  useEffect(() => {
    if (!isAuthenticated) {
      setState(null);
      setProfile(null);
      setKbProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchAll(false)
      .catch(() => {
        setState(null);
        setProfile(null);
        setKbProfile(null);
      })
      .finally(() => setLoading(false));
  }, [isAuthenticated, fetchAll]);

  const markStepCompleted = useCallback(async (step: MarkableStep) => {
    const next = await onboardingService.updateState({ step_completed: step });
    setState(next);
  }, []);

  const markWelcomeSeen = useCallback(() => {
    // Optimistic reveal — flip the local flag now so the card disappears
    // and the timeline shows without a round-trip (FR2). The server write is
    // fire-and-forget: if it fails the flag stays NULL server-side and the
    // card reappears on the next reload (the safe fallback).
    setState((prev) =>
      prev && prev.welcome_seen_at === null
        ? { ...prev, welcome_seen_at: new Date().toISOString() }
        : prev
    );
    void onboardingService
      .markWelcomeSeen()
      .then(setState)
      .catch(() => {});
  }, []);

  const savePartialKb = useCallback(async (payload: Record<string, unknown> | null) => {
    const next = await onboardingService.updateState({ partial_kb_form: payload });
    setState(next);
  }, []);

  const submitKbProfile = useCallback(async (payload: KbProfileSubmitRequest) => {
    const next = await kbProfileService.submit(payload);
    setState(next);
    // The submit cleared the draft + committed the profile; re-read both so
    // the timeline summary + a later re-edit see fresh data.
    const [nextProfile, nextKbProfile] = await Promise.all([
      tenantProfileService.get(),
      kbProfileService.get(),
    ]);
    setProfile(nextProfile);
    setKbProfile(nextKbProfile);
  }, []);

  const updateKbProfile = useCallback(async (changes: KbProfileUpdateRequest) => {
    const updated = await kbProfileService.update(changes);
    setKbProfile(updated);
  }, []);

  const value: OnboardingContextValue = {
    state,
    profile,
    kbProfile,
    loading,
    refresh,
    markStepCompleted,
    markWelcomeSeen,
    savePartialKb,
    submitKbProfile,
    updateKbProfile,
    metaOauthOutcome,
    setMetaOauthOutcome,
    stripeOutcome,
    setStripeOutcome,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOnboardingState(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboardingState must be used inside <OnboardingProvider>');
  return ctx;
}
